import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { history } from '../store'

import { searchModuleBySN, changeSN } from '../actions/modules'
import { fetchMachines } from '../actions/machines'
import { addUniversalUpdateSingleParameters, addUniversalUpdateSingleReboot } from '../actions/universalupdates'

import LoadingProgress from '../components/LoadingProgress'
import { QrReader } from 'react-qr-reader'

import '../styles/ChangeSNView.css'

class ChangeSNView extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      oldSN: '',
      newSN: '',
      foundModules: null,
      selectedModuleCode: null,
      step: 'input',       // 'input' | 'confirm_sn' | 'confirm_update' | 'done'
      loading: false,
      error: null,
      changedMachineId: null,
      changedMachineSerial: null,
      machinesReady: false,
    }
  }

  componentDidMount() {
    // Always fetch the full machines list (includes Modules field).
    // fetchMachinesBasic from other views only returns Serial+LastIndex,
    // so we must not reuse that cached data for the module→machine lookup.
    this.props.fetchMachines()
      .then(() => this.setState({ machinesReady: true }))
      .catch(() => this.setState({ machinesReady: true }))
  }

  handleCheck = async () => {
    const { oldSN, newSN } = this.state
    const paddedOld = oldSN.trim().padStart(10, '0')
    const paddedNew = newSN.trim().padStart(10, '0')

    if (!paddedOld) {
      this.setState({ error: 'Please enter the old SN.' })
      return
    }

    this.setState({ loading: true, error: null, foundModules: null, oldSN: paddedOld, newSN: paddedNew })

    try {
      const response = await this.props.searchModuleBySN(paddedOld)

      if (response.error) {
        this.setState({ error: response.error.message, loading: false })
        return
      }

      const modules = response.response
      if (!modules || modules.length === 0) {
        this.setState({ error: 'No modules found with this SN.', loading: false })
        return
      }

      this.setState({
        foundModules: modules,
        selectedModuleCode: modules.length === 1 ? modules[0].Code : null,
        step: 'confirm_sn',
        loading: false,
      })
    } catch (e) {
      this.setState({ error: 'Error searching for modules.', loading: false })
    }
  }

  getMachineForModule(moduleCode) {
    const { machines } = this.props
    if (!machines) return null
    return machines.find(m =>
      m.Modules && m.Modules.some(mod => mod.serialNumber === moduleCode)
    ) || null
  }

  handleConfirmChange = async () => {
    const { selectedModuleCode, oldSN, newSN } = this.state

    if (!selectedModuleCode) {
      this.setState({ error: 'Please select a module.' })
      return
    }

    if (!newSN.trim()) {
      this.setState({ error: 'Please enter the new SN.' })
      return
    }

    this.setState({ loading: true, error: null })

    try {
      const response = await this.props.changeSN(selectedModuleCode, oldSN.trim(), newSN.trim())

      if (response.error) {
        this.setState({ error: response.error.message, loading: false })
        return
      }

      const machine = this.getMachineForModule(selectedModuleCode)

      this.setState({
        step: 'confirm_update',
        loading: false,
        changedMachineId: machine ? machine._id : null,
        changedMachineSerial: machine ? machine.Serial : null,
      })
    } catch (e) {
      this.setState({ error: 'Error changing SN.', loading: false })
    }
  }

  handleUpdateMachine = async () => {
    const { changedMachineId } = this.state

    if (!changedMachineId) {
      this.setState({ step: 'done' })
      return
    }

    this.setState({ loading: true })

    const { role, fullname } = this.props
    const executor = `CAMBIO SN: ${fullname || ''}`.trim()
    await this.props.addUniversalUpdateSingleParameters(changedMachineId)
    await this.props.addUniversalUpdateSingleReboot(changedMachineId, executor)

    this.setState({ step: 'done', loading: false })
  }

  handleSkipUpdate = () => {
    this.setState({ step: 'done' })
  }

  handleReset = () => {
    this.setState({
      oldSN: '',
      newSN: '',
      foundModules: null,
      selectedModuleCode: null,
      step: 'input',
      loading: false,
      error: null,
      changedMachineId: null,
      changedMachineSerial: null,
    })
  }

  /* ── Helpers ──────────────────────────────────────── */

  renderStepsHeader(currentStep) {
    const steps = [
      { key: 'input',          label: 'Introducir' },
      { key: 'confirm_sn',     label: 'Seleccionar' },
      { key: 'confirm_update', label: 'Confirmar' },
      { key: 'done',           label: 'Listo' },
    ]
    const currentIdx = steps.findIndex(s => s.key === currentStep)

    return (
      <div className="csn-steps-header">
        {steps.map((s, i) => {
          const isDone   = i < currentIdx
          const isActive = i === currentIdx
          return (
            <React.Fragment key={s.key}>
              <div className={`csn-step-h ${isDone ? 'done' : ''} ${isActive ? 'active' : ''}`}>
                <div className="csn-step-h-dot">
                  {isDone ? <i className="fas fa-check" style={{ fontSize: 9 }} /> : i + 1}
                </div>
                <span className="csn-step-h-label">{s.label}</span>
              </div>
              {i < steps.length - 1 && (
                <div className={`csn-step-h-line ${i < currentIdx ? 'done' : ''}`} />
              )}
            </React.Fragment>
          )
        })}
      </div>
    )
  }

  /* ── QR modal ──────────────────────────────────────── */

  renderQRModal() {
    const { scanTarget } = this.state
    if (!scanTarget) return null

    return (
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16,
        }}
        onClick={() => this.setState({ scanTarget: null })}
      >
        <div
          style={{
            background: 'white', borderRadius: 16, overflow: 'hidden',
            width: '100%', maxWidth: 380,
            boxShadow: '0 24px 48px rgba(0,0,0,0.4)',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Modal header */}
          <div style={{
            background: 'linear-gradient(135deg,#022040,#0a4080)',
            padding: '14px 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <i className="fas fa-qrcode" style={{ color: 'white', fontSize: 18 }}></i>
              <span style={{ color: 'white', fontWeight: 700, fontSize: 15 }}>
                Escanear SN {scanTarget === 'old' ? 'Actual' : 'Nuevo'}
              </span>
            </div>
            <button
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}
              onClick={() => this.setState({ scanTarget: null })}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          {/* Camera view */}
          <div style={{ position: 'relative' }}>
            <QrReader
              onResult={(result) => {
                if (result) {
                  const sn = result?.text?.slice(-8) || result?.text
                  if (scanTarget === 'old') {
                    this.setState({ oldSN: sn, scanTarget: null, error: null })
                  } else {
                    this.setState({ newSN: sn, scanTarget: null, error: null })
                  }
                }
              }}
              constraints={{ facingMode: 'environment' }}
              style={{ width: '100%' }}
            />
            <div style={{
              position: 'absolute', inset: 0, pointerEvents: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{
                width: 180, height: 180,
                border: '3px solid rgba(255,255,255,0.8)',
                borderRadius: 12,
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)',
              }} />
            </div>
          </div>

          <div style={{ padding: '12px 20px', textAlign: 'center', fontSize: 12, color: '#64748b' }}>
            Apunta el QR al recuadro — se detectará automáticamente
          </div>
        </div>
      </div>
    )
  }

  /* ── Steps ────────────────────────────────────────── */

  renderInputStep() {
    const { oldSN, newSN, loading, error } = this.state

    return (
      <>
        {/* Instructions card */}
        <div className="csn-card" style={{ marginBottom: 16 }}>
          <div className="csn-card-body" style={{ paddingTop: 20, paddingBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg,#022040,#0a4080)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="fas fa-info" style={{ color: 'white', fontSize: 16 }}></i>
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#022040', marginBottom: 6 }}>Cambio de número de serie</div>
                <p style={{ margin: 0, fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
                  Introduce el <strong>SN actual</strong> (impreso bajo el QR del locker a retirar) y el <strong>nuevo SN</strong> (del locker de reemplazo).
                  El sistema buscará el módulo correspondiente y actualizará el registro automáticamente.
                </p>
                <div style={{ display: 'flex', gap: 20, marginTop: 10 }}>
                  <span style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <i className="fas fa-qrcode" style={{ color: '#f59e0b' }}></i> El SN está bajo el código QR
                  </span>
                  <span style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <i className="fas fa-keyboard" style={{ color: '#022040' }}></i> Puedes escanear o escribir manualmente
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {this.renderQRModal()}

        {/* Input card — no header */}
        <div className="csn-card">
          <div className="csn-card-body">

            <div className="csn-sn-panels">

              {/* Old SN */}
              <div className="csn-sn-panel old">
                <img src="/images/locker.png" alt="Locker" className="csn-sn-panel-thumb" />
                <div className="csn-sn-panel-body">
                  <span className="csn-sn-panel-label">
                    <i className="fas fa-tag"></i> SN Actual
                  </span>
                  <p className="csn-sn-panel-hint">SN impreso bajo el código QR en el locker a reemplazar</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="text"
                      className="csn-sn-panel-input"
                      placeholder="0000000000"
                      value={oldSN}
                      onChange={e => this.setState({ oldSN: e.target.value, error: null })}
                      autoFocus
                    />
                    <button
                      className="csn-qr-scan-btn"
                      onClick={() => this.setState({ scanTarget: 'old' })}
                      title="Escanear QR"
                      type="button"
                    >
                      <i className="fas fa-qrcode"></i>
                      <span>Escanear QR</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="csn-panel-divider">
                <i className="fas fa-arrow-right"></i>
              </div>

              {/* New SN */}
              <div className="csn-sn-panel new">
                <img src="/images/locker.png" alt="Locker" className="csn-sn-panel-thumb" />
                <div className="csn-sn-panel-body">
                  <span className="csn-sn-panel-label">
                    <i className="fas fa-tag"></i> Nuevo SN
                  </span>
                  <p className="csn-sn-panel-hint">SN del locker de reemplazo</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="text"
                      className="csn-sn-panel-input"
                      placeholder="0000000000"
                      value={newSN}
                      onChange={e => this.setState({ newSN: e.target.value, error: null })}
                    />
                    <button
                      className="csn-qr-scan-btn"
                      onClick={() => this.setState({ scanTarget: 'new' })}
                      title="Escanear QR"
                      type="button"
                    >
                      <i className="fas fa-qrcode"></i>
                      <span>Escanear QR</span>
                    </button>
                  </div>
                </div>
              </div>

            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            <div className="csn-actions">
              <button
                className="csn-btn secondary"
                onClick={() => this.setState({ oldSN: '', newSN: '', error: null })}
                disabled={!oldSN && !newSN}
              >
                <i className="fas fa-times"></i>
                <span>Limpiar</span>
              </button>
              <button
                className="csn-btn primary"
                onClick={this.handleCheck}
                disabled={loading || oldSN.trim().length < 8 || newSN.trim().length < 8}
              >
                {loading
                  ? <span className="spinner-border spinner-border-sm"></span>
                  : <i className="fas fa-search"></i>
                }
                <span>Verificar SN</span>
              </button>
            </div>
          </div>
        </div>
      </>
    )
  }

  renderConfirmSNStep() {
    const { foundModules, selectedModuleCode, oldSN, newSN, loading, error } = this.state

    return (
      <div className="csn-card">
        <div className="csn-card-header">
          <i className="fas fa-list-ul"></i>
          <h3>Selecciona el módulo a modificar</h3>
        </div>
        <div className="csn-card-body">

          {/* SN change summary */}
          <div className="csn-change-row">
            <div className="csn-change-badge old">
              <i className="fas fa-tag"></i>
              <code>{oldSN}</code>
            </div>
            <i className="fas fa-arrow-right csn-change-arrow"></i>
            <div className="csn-change-badge new">
              <i className="fas fa-tag"></i>
              <code>{newSN}</code>
            </div>
            <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}>
              {foundModules.length} módulo(s) encontrado(s)
            </span>
          </div>

          {/* Module selection cards */}
          <div className="csn-module-grid">
            {foundModules.map(mod => {
              const machine  = this.getMachineForModule(mod.Code)
              const snPadded = oldSN.trim().padStart(10, '0')
              const isSelected = selectedModuleCode === mod.Code
              return (
                <div
                  key={mod.Code}
                  className={`csn-module-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => this.setState({ selectedModuleCode: mod.Code, error: null })}
                >
                  <div className="csn-module-radio">
                    {isSelected && <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'white' }} />}
                  </div>
                  <span className="csn-module-code">{mod.Code}</span>
                  <div className="csn-module-meta">
                    {machine ? (
                      <span className="csn-module-machine">
                        <i className="fas fa-desktop"></i>{machine.Serial}
                      </span>
                    ) : (
                      <span style={{ fontSize: 12, color: '#94a3b8' }}>Sin máquina asignada</span>
                    )}
                    {mod.RegisterLockers && mod.RegisterLockers.map((lock, i) => (
                      <span
                        key={i}
                        className="badge badge-pill"
                        style={{
                          background: lock.SN === snPadded ? '#fef3c7' : '#f1f5f9',
                          color: lock.SN === snPadded ? '#92400e' : '#475569',
                          border: `1px solid ${lock.SN === snPadded ? '#fbbf24' : '#e2e8f0'}`,
                          fontSize: 11,
                          fontFamily: 'monospace',
                          marginRight: 4,
                        }}
                      >
                        {lock.Position}: {lock.SN}
                      </span>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          <div className="csn-actions">
            <button className="csn-btn secondary" onClick={this.handleReset}>
              <i className="fas fa-arrow-left"></i>
              <span>Volver</span>
            </button>
            <button
              className="csn-btn"
              style={{ background: 'linear-gradient(135deg,#dc2626,#ef4444)', color: 'white' }}
              onClick={this.handleConfirmChange}
              disabled={loading || !selectedModuleCode}
            >
              {loading
                ? <span className="spinner-border spinner-border-sm"></span>
                : <i className="fas fa-check"></i>
              }
              <span>Confirmar cambio</span>
            </button>
          </div>
        </div>
      </div>
    )
  }

  renderConfirmUpdateStep() {
    const { oldSN, newSN, selectedModuleCode, changedMachineSerial, loading } = this.state

    return (
      <div className="csn-card">
        <div className="csn-card-header">
          <i className="fas fa-check-circle"></i>
          <h3>SN cambiado — ¿actualizar máquina?</h3>
        </div>
        <div className="csn-card-body">

          {/* Change summary */}
          <div className="csn-change-row">
            <div className="csn-change-badge old">
              <i className="fas fa-tag"></i>
              <code>{oldSN}</code>
            </div>
            <i className="fas fa-arrow-right csn-change-arrow"></i>
            <div className="csn-change-badge new">
              <i className="fas fa-tag"></i>
              <code>{newSN}</code>
            </div>
            <span style={{ fontSize: 12, color: '#16a34a', marginLeft: 'auto', fontWeight: 600 }}>
              <i className="fas fa-check-circle mr-1"></i>Módulo {selectedModuleCode}
            </span>
          </div>

          {changedMachineSerial ? (
            <>
              <div className="csn-machine-block">
                <div className="csn-machine-icon">
                  <i className="fas fa-desktop"></i>
                </div>
                <div className="csn-machine-info">
                  <strong>{changedMachineSerial}</strong>
                  <p>Esta máquina tiene el módulo modificado. ¿Enviar actualización de parámetros y reboot remoto ahora?</p>
                  <p className="csn-machine-warn">
                    <i className="fas fa-info-circle mr-1"></i>
                    Si vas a cambiar más SNs en esta máquina, espera al último cambio.
                  </p>
                </div>
              </div>

              <div className="csn-actions">
                <button className="csn-btn secondary" onClick={this.handleSkipUpdate} disabled={loading}>
                  <i className="fas fa-clock"></i>
                  <span>No actualizar ahora</span>
                </button>
                <button className="csn-btn primary" onClick={this.handleUpdateMachine} disabled={loading}>
                  {loading
                    ? <span className="spinner-border spinner-border-sm"></span>
                    : <i className="fas fa-sync-alt"></i>
                  }
                  <span>Actualizar y reboot</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="csn-status-card info">
                <i className="fas fa-info-circle"></i>
                <p>El módulo no está asignado a ninguna máquina. No se requiere actualización remota.</p>
              </div>
              <div className="csn-actions">
                <button className="csn-btn primary" onClick={this.handleSkipUpdate}>
                  <i className="fas fa-check"></i>
                  <span>Continuar</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  renderDoneStep() {
    const { changedMachineSerial, newSN, selectedModuleCode } = this.state

    return (
      <div className="csn-card">
        <div className="csn-card-body">
          <div className="csn-done-banner">
            <div className="csn-done-icon">
              <i className="fas fa-check"></i>
            </div>
            <div className="csn-done-title">Cambio completado</div>
            <p className="csn-done-sub">
              El nuevo SN <strong>{newSN}</strong> ha sido asignado al módulo <strong>{selectedModuleCode}</strong>.
              {changedMachineSerial && (
                <> La máquina <strong>{changedMachineSerial}</strong> recibirá la actualización en el próximo ciclo.</>
              )}
            </p>
          </div>

          <div className="csn-actions">
            <button className="csn-btn secondary" onClick={this.handleReset}>
              <i className="fas fa-exchange-alt"></i>
              <span>Cambiar otro SN</span>
            </button>
            <button className="csn-btn primary" onClick={() => history.push('/modules')}>
              <i className="fas fa-th-large"></i>
              <span>Ir a Módulos</span>
            </button>
          </div>
        </div>
      </div>
    )
  }

  render() {
    const { step, machinesReady } = this.state

    if (!machinesReady) {
      return <LoadingProgress />
    }

    return (
      <div className="change-sn-view">

        {/* Header */}
        <div className="csn-header">
          <div className="csn-header-content">
            <div className="csn-header-text">
              <h1>
                <i className="fas fa-exchange-alt mr-3"></i>
                <span className="text-vw-dark mr-1">Cambio de</span>
                <span className="text-vw-light">SN</span>
              </h1>
            </div>
            <div className="csn-header-actions">
              {this.renderStepsHeader(step)}
            </div>
          </div>
        </div>

        {/* Step content */}
        <div className="row">
          <div className="col-12">
            {step === 'input'          && this.renderInputStep()}
            {step === 'confirm_sn'     && this.renderConfirmSNStep()}
            {step === 'confirm_update' && this.renderConfirmUpdateStep()}
            {step === 'done'           && this.renderDoneStep()}            
          </div>
        </div>

      </div>
    )
  }
}

const mapStateToProps = state => ({
  role: state.auth.role,
  fullname: state.auth.fullname,
  machines: state.machines.machines,
})

const mapDispatchToProps = dispatch => ({
  searchModuleBySN: bindActionCreators(searchModuleBySN, dispatch),
  changeSN: bindActionCreators(changeSN, dispatch),
  fetchMachines: bindActionCreators(fetchMachines, dispatch),
  addUniversalUpdateSingleParameters: bindActionCreators(addUniversalUpdateSingleParameters, dispatch),
  addUniversalUpdateSingleReboot: bindActionCreators(addUniversalUpdateSingleReboot, dispatch),
})

export default connect(mapStateToProps, mapDispatchToProps)(ChangeSNView)
